import React, { useEffect } from "react";
import { Phone, Mail, MapPin } from "lucide-react";

/* ── Scroll-reveal hook ── */
function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => e.isIntersecting && e.target.classList.add("sr-visible")),
      { threshold: 0.12 }
    );
    document.querySelectorAll(".sr").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

const Support = () => {
  useScrollReveal();

  const cards = [
    {
      icon: <MapPin size={28} />,
      title: "Visit Office",
      desc: "Constituency Bursary Office, Igembe South. Monday – Friday, 8:00 AM – 5:00 PM.",
      label: "Get Directions",
      href: "https://goo.gl/maps/example",
      external: true,
      delay: "0s",
    },
    {
      icon: <Phone size={28} />,
      title: "Call Us",
      desc: "Contact our bursary office directly for assistance or inquiries.",
      label: "Call Now",
      href: "tel:+254700000000",
      external: false,
      delay: "0.1s",
    },
    {
      icon: <Mail size={28} />,
      title: "Email Support",
      desc: "Send us an email and our support team will respond promptly.",
      label: "Email Us",
      href: "mailto:bursary@igembesouth.go.ke",
      external: false,
      delay: "0.2s",
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-20">
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6 text-center">

          {/* Heading */}
          <div className="sr" style={{ transitionDelay: "0s" }}>
            <h2 className="text-3xl md:text-4xl font-bold mb-3">Need Help?</h2>
          </div>
          <div className="sr" style={{ transitionDelay: "0.08s" }}>
            <p className="text-gray-700 text-lg mb-12 max-w-2xl mx-auto">
              If you need guidance or assistance with your bursary application, you can reach out through any of the options below.
            </p>
          </div>

          <div className="grid sm:grid-cols-1 md:grid-cols-3 gap-8">
            {cards.map((card, i) => (
              <div
                key={i}
                className="sr group bg-white rounded-2xl shadow-md p-8 flex flex-col items-center text-center border border-gray-200 hover:shadow-xl hover:-translate-y-2 hover:border-blue-200 transition-all duration-300 relative overflow-hidden"
                style={{ transitionDelay: card.delay }}
              >
                {/* ripple bg */}
                <div className="absolute inset-0 bg-blue-50 scale-0 group-hover:scale-100 transition-transform duration-500 rounded-2xl origin-center" />

                <div className="relative z-10 bg-blue-700 text-white w-16 h-16 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 group-hover:-rotate-6 transition-transform duration-300">
                  {card.icon}
                </div>
                <h3 className="relative z-10 text-xl font-semibold mb-2">{card.title}</h3>
                <p className="relative z-10 text-gray-700 mb-4 text-sm flex-grow">{card.desc}</p>
                <a
                  href={card.href}
                  target={card.external ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="relative z-10 mt-auto bg-blue-700 text-white py-2 px-5 rounded-lg hover:bg-blue-600 active:scale-95 transition-all duration-200 text-sm font-medium"
                >
                  {card.label}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <style>{`
        .sr { opacity: 0; transform: translateY(24px); transition: opacity 0.55s ease, transform 0.55s ease; }
        .sr-visible { opacity: 1; transform: translateY(0); }
      `}</style>
    </div>
  );
};

export default Support;